<template>
  <div class="app-main">
    <intro-video
      v-if="showIntro"
      @skip-intro="onSkipIntro"
    />
    <div class="main-view-container" v-else-if="endUserProfileStore.isLoggedIn && applicationStore.hasEnoughMemory">
      <main-view
      :isViewLoaded="isMainViewLoaded"/>
      <transition name="app-main__fade-transition">
        <main-loading-screen class="main-view-container__loading-screen"
        v-show="!isMainViewLoaded"/>
      </transition>
    </div>
    <not-enough-memory v-else-if="!applicationStore.hasEnoughMemory"/>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { fonts } from '@/static/fonts'
import MainView from '@/components/MainView.vue'
import NotEnoughMemory from '@/components/unsupported-pages/NotEnoughMemory.vue'
import IntroVideo from '@/components/onboarding/IntroVideo.vue'
import MainLoadingScreen from '@/components/MainLoadingScreen.vue'
import { swsplugin } from '@/auth/plugin'
import { app } from '@/main'
import { useApplicationStore } from '@/store/application-store'
import { useEndUserProfileStore } from '@/store/user-profile'
import { usePersistedStorage } from '@/composables/UsePersistedStorage'
import GtmHelper from '@/analytics/gtm-helper'

const onboardingVideoCompletedKey = 'onboarding.video.completed'
const applicationStore = useApplicationStore()
const endUserProfileStore = useEndUserProfileStore()
const { persistedStorage } = usePersistedStorage()
const isMainViewLoaded = computed(() => {
  return applicationStore.isPreviewCanvasLoaded && applicationStore.isActionMenuLoaded
})

const authenticateUser = () => {
  app.use(swsplugin)
}

const showIntro = ref(persistedStorage.getItem(onboardingVideoCompletedKey) !== 'true')
const onSkipIntro = () => {
  persistedStorage.setItem(onboardingVideoCompletedKey, 'true')
  showIntro.value = false
  authenticateUser()
}
// add userId watcher to enable GTM AFTER we fetch user id if GTM is enabled for this env
const isGtmEnabled = import.meta.env.VUE_APP_GTM_ENABLED === 'true'
if (isGtmEnabled) {
  watch(() => endUserProfileStore.userId, () => {
    const userId = Number(endUserProfileStore.userId)
    const gtmHelper = GtmHelper.getInstance()

    // enable GTM only if
    // - User ID has been fetched
    // - GTM helper has not been enabled yet
    if (userId > 0 && !gtmHelper.isEnabled()) {
      // Enable GTM helper
      gtmHelper.enable()
      // Add userID to datalayer for GTM
      window.dataLayer?.push({
        'userId': userId
      })
    }
  })
}

onMounted(async () => {
  window.onbeforeunload = function () {
    /*
      This message can't be customized for the majority of new browsers,
      but we still have to return a message to make it work.
    */
    return isMainViewLoaded.value ? 'Changes that you made may not be saved.' : undefined
  }

  if (!showIntro.value) {
    authenticateUser()
  }
  // Wait for initially used fonts (Helvetica Neue) to be loaded first
  await document.fonts.ready

  // Load all custom fonts used by the canvas
  for (const font of fonts) {
    document.fonts.load(`1em ${font.name}`)
  }
})
</script>

<style lang="scss">
#app, .app-main, .main-view-container {
  height: 100vh;
  background-color: $black;
  user-select: none;

  &__loading-screen {
    margin: auto;
    height: 100%;
    width: 100%;
    padding-top: 20px;
    z-index: 2;
    background: $grey-90;
    position: absolute;
    top: 0px;
  }

  .main-view-container, &__loading-screen {
    min-width: 1200px;
    min-height: 695px;
    overflow: hidden;
  }

  &__fade-transition {
    &-enter-active,
    &-leave-active {
      transition: opacity 200ms $animationCurve;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
}
</style>
